import dayjs, { type Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import styled from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export interface AutoSelectOptionType {
  label: string;
  func: () => [Dayjs, Dayjs];
  isSelected: (startDate: Dayjs, endDate: Dayjs) => boolean;
}

export const autoSelectOptions: Record<string, AutoSelectOptionType> = {
  today: {
    label: "Today",
    func: () => [dayjs(), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs(), "day") && endDate.isSame(dayjs(), "day"),
  },
  yesterday: {
    label: "Yesterday",
    func: () => [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().subtract(1, "day"), "day") &&
      endDate.isSame(dayjs().subtract(1, "day"), "day"),
  },
  tomorrow: {
    label: "Tomorrow",
    func: () => [dayjs().add(1, "day"), dayjs().add(1, "day")],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().add(1, "day"), "day") &&
      endDate.isSame(dayjs().add(1, "day"), "day"),
  },
  last7Days: {
    label: "Last 7 days",
    func: () => [dayjs().subtract(6, "day"), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().subtract(6, "day"), "day") &&
      endDate.isSame(dayjs(), "day"),
  },
  last30Days: {
    label: "Last 30 days",
    func: () => [dayjs().subtract(29, "day"), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().subtract(29, "day"), "day") &&
      endDate.isSame(dayjs(), "day"),
  },
  last365Days: {
    label: "Last 365 days",
    func: () => [dayjs().subtract(364, "day"), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().subtract(364, "day"), "day") &&
      endDate.isSame(dayjs(), "day"),
  },
  lastCalendarYear: {
    label: "Last calendar year",
    func: () => [
      dayjs().startOf("year").subtract(1, "year"),
      dayjs().startOf("year").subtract(1, "day"),
    ],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("year").subtract(1, "year"), "day") &&
      endDate.isSame(dayjs().startOf("year").subtract(1, "day"), "day"),
  },
  previousMonth: {
    label: "Previous month",
    func: () => [
      dayjs().startOf("month").subtract(1, "month"),
      dayjs().startOf("month").subtract(1, "day"),
    ],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("month").subtract(1, "month"), "day") &&
      endDate.isSame(dayjs().startOf("month").subtract(1, "day"), "day"),
  },
  previous3Months: {
    label: "Previous 3 months",
    func: () => [
      dayjs().startOf("month").subtract(3, "month"),
      dayjs().startOf("month").subtract(1, "day"),
    ],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("month").subtract(3, "month"), "day") &&
      endDate.isSame(dayjs().startOf("month").subtract(1, "day"), "day"),
  },
  previous6Months: {
    label: "Previous 6 months",
    func: () => [
      dayjs().startOf("month").subtract(6, "month"),
      dayjs().startOf("month").subtract(1, "day"),
    ],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("month").subtract(6, "month"), "day") &&
      endDate.isSame(dayjs().startOf("month").subtract(1, "day"), "day"),
  },
  previous12Months: {
    label: "Previous 12 months",
    func: () => [
      dayjs().startOf("month").subtract(1, "year"),
      dayjs().startOf("month").subtract(1, "day"),
    ],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("month").subtract(1, "year"), "day") &&
      endDate.isSame(dayjs().startOf("month").subtract(1, "day"), "day"),
  },
  monthToDate: {
    label: "Month to date",
    func: () => [dayjs().startOf("month"), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("month"), "day") &&
      endDate.isSame(dayjs(), "day"),
  },
  yearToDate: {
    label: "Year to date",
    func: () => [dayjs().startOf("year"), dayjs()],
    isSelected: (startDate: Dayjs, endDate: Dayjs) =>
      startDate.isSame(dayjs().startOf("year"), "day") &&
      endDate.isSame(dayjs(), "day"),
  },
};

export const DatePickerWrapper = styled.div`
  .react-datepicker__month-container {
    width: 50%;
  }
  .react-datepicker,
  .react-datepicker__header {
    width: 100%;
    border: none;
    background-color: ${colorTheme("white")};
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  .react-datepicker__day--keyboard-selected {
    border-color: ${colorTheme("white")};
    background-color: ${colorTheme("white")};
    color: ${colorTheme("black")};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-range,
  .react-datepicker__day:focus,
  .react-datepicker__day:active,
  .react-datepicker__day:hover {
    background-color: ${colorTheme("primary")};
    border-color: ${colorTheme("primary")};
    color: ${colorTheme("white")};
    outline: none;
  }

  .react-datepicker__day--in-selecting-range {
    background-color: ${colorTheme("primaryL1")};
    border-color: ${colorTheme("primaryL1")};
  }

  .react-datepicker__day-name,
  .react-datepicker__day {
    margin: ${gridSpacing[1]}px;
    border-radius: 1000px;
  }

  .react-datepicker__day-name {
    color: ${colorTheme("neutralL1")};
  }

  .react-datepicker__navigation-icon {
    left: 0;

    ::before {
      border-color: ${colorTheme("neutral")};
      border-width: 2px 2px 0 0;
      height: 8px;
      width: 8px;
      top: 11px;
    }
  }

  .react-datepicker__navigation {
    margin-top: 5px;
    height: 16px;
    width: 16px;
    padding: 6px;
    border-radius: 1000px;

    &:hover {
      background-color: ${colorTheme("neutralL5")};
    }
  }

  .react-datepicker__current-month {
    margin-bottom: ${gridSpacing[2]}px;
    color: ${colorTheme("neutral")};
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--selected {
    border-radius: 1000px;
  }

  .date-picker-class {
    background-color: ${colorTheme("white")};
    cursor: pointer;
    padding: 3px 10px;
    color: ${colorTheme("neutral")};
    border: 1px solid ${colorTheme("neutralL4")};
    border-radius: 5px;
    width: calc(100% - 20px);
    display: flex;
    height: 30px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;

    &::placeholder {
      color: ${colorTheme("neutralL2")};
    }

    &:hover:not(:disabled) {
      border-color: ${colorTheme("neutralL3")};
    }

    &:focus:not(:disabled),
    &:active:not(:disabled) {
      outline: none;
      border-color: ${colorTheme("primary")};
      box-shadow: 0 0 0 1px ${colorTheme("primaryL1")};
    }

    &:disabled {
      background-color: ${colorTheme("neutralL4")};
      cursor: not-allowed;
      color: ${colorTheme("neutralL2")};
      border-color: ${colorTheme("neutralL3")};

      &::placeholder {
        color: ${colorTheme("neutralL2")};
      }
    }
  }
`;

export type AutoSelectsValue = keyof typeof autoSelectOptions;
